import { createContext, useContext } from 'react';
import type { ConfigurationApiClient, ConfigurationScope } from './types';

/**
 * What the configuration hooks need: how to call the API as the logged-in user, which app this
 * is, who is looking at which org node, and what they may do. `PlatformProvider` supplies it from
 * the session; tests (and anything mounted without a session) can supply it directly.
 */
export interface ConfigurationContextValue {
  client: ConfigurationApiClient;
  /** Sent as `X-App-Id` on the layout request, e.g. `dealer-portal`. */
  appId: string;
  /** Null while logged out: nothing is fetched. */
  scope: ConfigurationScope | null;
  /** ADMIN role at GLOBAL scope (from `/auth/whoami`). */
  isGlobalAdmin: boolean;
  /** Effective permissions at the selected node (`/me/access`); undefined while they load. */
  permissions: readonly string[] | undefined;
}

export const ConfigurationContext = createContext<ConfigurationContextValue | null>(null);

export function useConfigurationContext(): ConfigurationContextValue {
  const value = useContext(ConfigurationContext);
  if (!value) {
    throw new Error('Configuration hooks must be used inside <ConfigurationProvider>');
  }
  return value;
}
